import { createApp, watch } from "vue";
import { FrappeUI } from "frappe-ui";
import App from "./App.vue";
import router from "./router";
import { initTheme } from "./lib/theme";
import { initAppearance } from "./lib/appearanceRuntime";
import { consumeErpSignIn, onErpReauthNeeded } from "./lib/erpSignIn";
import { currentConversationId } from "./lib/live";
import { currentUser, token } from "./lib/api";
import "./index.css";
import "./styles/otto.css";
import { initTheme as initWallpaper } from "./lib/ottoThemes";

// Apply the remembered wallpaper theme before the app paints.
initWallpaper();

// Light/dark mode: stamps data-theme and wires the watcher the Mode switch
// drives. Must run — a name clash with the wallpaper init above used to shadow
// it, so the Mode toggle changed nothing.
initTheme();
initAppearance();

// Preserve an ERPNext companion handoff while the operator signs in. The
// signed values are verified by the Genie server after authentication.
const companion = new URLSearchParams(window.location.search);
if (companion.has("companion_context") && companion.has("companion_signature")) {
  sessionStorage.setItem("genie.companion", JSON.stringify({
    context: companion.get("companion_context"),
    signature: companion.get("companion_signature"),
  }));
  window.history.replaceState(null, "", window.location.pathname);
}

// Before the router mounts: an ERP sign-in returns with the session in the URL
// fragment, and the route guard has to see the token or it will bounce a
// perfectly good login straight back to the sign-in screen.
const erpLanding = consumeErpSignIn();

// Any screen's own ERP tool call can discover the credential has expired —
// not just the OAuth round trip above — and every screen should answer the
// same way: to the sign-in screen, not a scattered error message. See
// lib/erpSignIn.ts.
function toSignIn(): void {
  const current = router.currentRoute.value.fullPath;
  void router.push(current === "/" || current.startsWith("/signin") ? { name: "signin" } : { name: "signin", query: { next: current } });
}
onErpReauthNeeded(toSignIn);

// The Genie session itself can end under an open page — most often because a
// sign-in to another ERP site switched the deployment. The route guard only
// runs on navigation, so without this the screen stays up and every panel
// reports "Authentication required." instead of asking to sign in again.
watch(token, (value, previous) => {
  if (value || !previous || router.currentRoute.value.meta.public) return;
  currentUser.value = null;
  toSignIn();
});

// A window opened straight on /chat/:id must ask for THAT thread on its very
// first socket, not adopt the most recent one and swap a moment later. The
// socket opens as soon as the shell mounts — before the router has resolved
// the route — so the thread is read off the address bar here.
const thread = /^\/chat\/([^/]+)/.exec(location.pathname);
if (thread) currentConversationId.value = decodeURIComponent(thread[1]);

const app = createApp(App);

// frappe-ui is used purely as a component library here — its Frappe-specific
// data layer (socket.io, the `call` helper, resource fetching) would try to
// reach a bench backend that doesn't exist in this stack.
app.use(FrappeUI, { socketio: false, resources: false, call: false });
app.use(router);

if (erpLanding) {
  // Wait for the router before replacing, so the landing path is the first
  // entry rather than a second one the back button can return from.
  router.isReady().then(() => router.replace(erpLanding));
}

app.mount("#app");
